import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Bot,
  FileSpreadsheet,
  Download,
  Eye,
  RotateCcw,
  X
} from 'lucide-react';
import DocumentUpload from './components/DocumentUpload';
import DocumentValidation from './components/DocumentValidation';
import CifrasValidation from './components/CifrasValidation';
import StructureValidation from './components/StructureValidation';
import Reclassification from './components/Reclassification';
import FinalValidation from './components/FinalValidation';
import ExportResults from './components/ExportResults';
import FinancialAnalysisChat from './components/FinancialAnalysisChat';
import './styles/FinancialAnalysis.scss';
import './styles/FinancialAnalysisChat.scss';

const FinancialAnalysis = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [stepResults, setStepResults] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Effect para actualizar PDF cuando cambia el step
  useEffect(() => {
    if (showDocumentPreview) {
      const currentStepId = steps[currentStep]?.id;
      console.log(`📱 useEffect: Actualizando PDF para step: ${currentStepId}`);
      
      setTimeout(() => {
        const iframe = document.querySelector('iframe[title="Vista previa del documento financiero"]');
        if (iframe) {
          const newUrl = getPdfUrl();
          console.log(`🔄 useEffect: Actualizando iframe src a: ${newUrl}`);
          iframe.src = newUrl;
        }
      }, 100);
    }
  }, [currentStep, showDocumentPreview]);
  
  // Get PDF URL based on current step
  const getPdfUrl = (page = currentPdfPage) => {
    const currentStepId = steps[currentStep]?.id;
    
    // Debug log para verificar qué PDF se está cargando
    console.log(`📄 Cargando PDF para step: ${currentStepId}, página: ${page}`);
    
    switch (currentStepId) {
      case 'validation':
        // Sección de firmas - página específica
        const firmasUrl = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-9.pdf';
        console.log(`🔍 PDF de Firmas: ${firmasUrl}`);
        return firmasUrl;
      
      case 'reclassification':
        // Sección de Nota 4 - página específica
        const notaUrl = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-25%20(1).pdf';
        console.log(`📋 PDF de Nota 4: ${notaUrl}`);
        return notaUrl;
      
      default:
        // PDF original con navegación de páginas
        const normalUrl = `https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023.pdf#page=${page}&view=FitH&toolbar=0&navpanes=0`;
        console.log(`📖 PDF Normal página ${page}: ${normalUrl}`);
        return normalUrl;
    }
  };

  const steps = [
    {
      id: 'upload',
      title: 'Carga de Documentos',
      description: 'Subir documentos PDF para análisis',
      icon: Upload,
      component: DocumentUpload
    },
    {
      id: 'validation',
      title: 'Validación de Documentos',
      description: 'Verificación de firmas y cumplimiento',
      icon: CheckCircle,
      component: DocumentValidation
    },
    {
      id: 'cifras',
      title: 'Validación de Cifras',
      description: 'Análisis de unidades y consistencia numérica',
      icon: FileSpreadsheet,
      component: CifrasValidation
    },
    {
      id: 'reclassification',
      title: 'Reclasificación Automática',
      description: 'IA realiza reclasificación contable',
      icon: Bot,
      component: Reclassification
    },
    {
      id: 'final',
      title: 'Validación Final',
      description: 'Revisión de Excel consolidado',
      icon: Eye,
      component: FinalValidation
    },
    {
      id: 'export',
      title: 'Exportación y Resultados',
      description: 'Descarga de archivos y reporte',
      icon: Download,
      component: ExportResults
    }
  ];

  const handleStepClick = async (stepIndex) => {
    if (isTransitioning || stepIndex === currentStep) return;
    
    setIsTransitioning(true);
    setPdfLoading(true);
    
    // Small delay to ensure smooth transition
    setTimeout(() => {
      const newStepId = steps[stepIndex].id;
      console.log(`🔄 Cambiando a step: ${newStepId} (index: ${stepIndex})`);
      
      setCurrentStep(stepIndex);
      
      // Reset PDF page based on step type
      if (newStepId === 'validation' || newStepId === 'reclassification') {
        // For special sections, reset to page 1 but they'll show their specific PDFs
        setCurrentPdfPage(1);
      } else {
        // For normal sections, reset to page 1
        setCurrentPdfPage(1);
      }
      
      // Force update of the iframe with new PDF - múltiples intentos
      setTimeout(() => {
        const iframe = document.querySelector('iframe[title="Vista previa del documento financiero"]');
        if (iframe) {
          // Crear nueva URL basada en el step actual
          let newUrl = '';
          switch (newStepId) {
            case 'validation':
              newUrl = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-9.pdf';
              console.log(`🔍 Forzando carga de PDF de Firmas: ${newUrl}`);
              break;
            case 'reclassification':
              newUrl = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-25%20(1).pdf';
              console.log(`📋 Forzando carga de PDF de Nota 4: ${newUrl}`);
              break;
            default:
              newUrl = `https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023.pdf#page=1&view=FitH&toolbar=0&navpanes=0`;
              console.log(`📖 Forzando carga de PDF Normal: ${newUrl}`);
          }
          
          // Múltiples intentos para asegurar la carga
          iframe.src = newUrl;
          
          // Intento 2
          setTimeout(() => {
            if (iframe.src !== newUrl) {
              console.log(`🔄 Segundo intento de carga: ${newUrl}`);
              iframe.src = newUrl;
            }
          }, 100);
          
          // Intento 3
          setTimeout(() => {
            if (iframe.src !== newUrl) {
              console.log(`🔄 Tercer intento de carga: ${newUrl}`);
              iframe.src = newUrl;
            }
          }, 300);
        }
      }, 50);
      
      setIsTransitioning(false);
      setPdfLoading(false);
    }, 100);
  };

  const handleFilesUploaded = (files) => {
    setUploadedFiles(files);
    // Mostrar preview cuando hay archivos cargados
    if (files.length > 0) {
      setShowDocumentPreview(true);
      // Reset to page 1 when new files are uploaded
      setCurrentPdfPage(1);
      setPdfLoading(false);
    } else {
      setShowDocumentPreview(false);
    }
  };

  const handleStepComplete = (result) => {
    setStepResults(prev => ({
      ...prev,
      [steps[currentStep].id]: result
    }));
  };

  const handlePageChange = (pageNumber) => {
    const currentStepId = steps[currentStep]?.id;
    
    // For specific sections (validation, reclassification), ignore page changes
    if (currentStepId === 'validation' || currentStepId === 'reclassification') {
      return;
    }
    
    if (pageNumber === currentPdfPage) {
      return;
    }
    
    setPdfLoading(true);
    setCurrentPdfPage(pageNumber);
    
    // Update iframe with new page
    const iframe = document.querySelector('iframe[title="Vista previa del documento financiero"]');
    
    if (iframe) {
      const newSrc = getPdfUrl(pageNumber);
      iframe.src = newSrc;
      
      // Fallback retry
      setTimeout(() => {
        if (iframe.src !== newSrc) {
          iframe.src = newSrc;
        }
      }, 100);
    }
    
    // Reset loading state
    setTimeout(() => {
      setPdfLoading(false);
    }, 800);
  };

  const handleReset = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(0);
      setUploadedFiles([]);
      setStepResults({});
      setShowDocumentPreview(false);
      setCurrentPdfPage(1);
      setPdfLoading(false);
      setIsTransitioning(false);
    }, 100);
  };

  const handleNavigation = (direction) => {
    if (isTransitioning) return;
    
    const nextStep = direction === 'next' 
      ? Math.min(steps.length - 1, currentStep + 1)
      : Math.max(0, currentStep - 1);
    
    const isNextEnabled = nextStep === 0 || (uploadedFiles.length > 0 && nextStep <= currentStep + 1);
    
    if (isNextEnabled && nextStep !== currentStep) {
      handleStepClick(nextStep);
    }
  };

  const CurrentComponent = steps[currentStep].component;

  return (
    <div className="financial-analysis-container">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />
      {/* Header */}
      <div className="financial-analysis-header">
        <div className="header-content">
          <div className="title-section">
            <Bot className="header-icon" />
            <div>
              <h1>Analista Financiero</h1>
            </div>
          </div>
          
          <div className="demo-controls">
            <div className="files-summary">
              {uploadedFiles.length > 0 && (
                <span className="files-count">
                  {uploadedFiles.length} archivo(s) cargado(s)
                </span>
              )}
            </div>
            
            <button 
              className="control-btn" 
              onClick={handleReset}
              disabled={isTransitioning}
            >
              <RotateCcw size={18} />
              Reiniciar
            </button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="steps-container">
        <div className="steps-timeline">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const isEnabled = index === 0 || (uploadedFiles.length > 0 && index <= currentStep + 1);

            return (
              <div
                key={step.id}
                className={`step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isEnabled ? 'disabled' : ''} ${isTransitioning ? 'transitioning' : ''}`}
                onClick={() => isEnabled && !isTransitioning && handleStepClick(index)}
              >
                <div className="step-indicator">
                  <div className="step-icon">
                    <Icon size={20} />
                  </div>
                  <div className="step-connector" />
                </div>
                
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="analysis-content" style={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
        {/* Left Panel - Analysis Steps */}
        <div 
          className="content-container" 
          style={{ 
            width: showDocumentPreview ? '60%' : '100%', 
            transition: 'width 0.3s ease',
            borderRight: showDocumentPreview ? '1px solid #e5e7eb' : 'none'
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`step-${currentStep}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                transition: {
                  duration: 0.4,
                  ease: [0.4, 0, 0.2, 1]
                }
              }}
              exit={{ 
                opacity: 0, 
                scale: 0.95,
                transition: {
                  duration: 0.2,
                  ease: [0.4, 0, 1, 1]
                }
              }}
              className="step-content-wrapper"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%'
              }}
            >
              <CurrentComponent
                isActive={true}
                uploadedFiles={uploadedFiles}
                onFilesUploaded={handleFilesUploaded}
                onComplete={handleStepComplete}
                stepResults={stepResults}
                onShowPreview={() => setShowDocumentPreview(true)}
                onPageChange={handlePageChange}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right Panel - Document Preview */}
        <AnimatePresence>
          {showDocumentPreview && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '40%', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{
                backgroundColor: '#f8fafc',
                borderLeft: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Document Preview Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                    color: 'white', 
                    padding: '8px', 
                    borderRadius: '8px' 
                  }}>
                    <FileText size={18} />
                  </div>
                  <div>
                                         <h3 style={{ 
                       margin: '0 0 2px 0', 
                       fontSize: '16px', 
                       fontWeight: '600', 
                       color: '#1f2937' 
                     }}>
                       Vista Previa
                       {steps[currentStep]?.id === 'validation' && (
                         <span style={{
                           marginLeft: '8px',
                           fontSize: '12px',
                           fontWeight: '500',
                           color: '#dc2626',
                           background: '#fef2f2',
                           padding: '2px 6px',
                           borderRadius: '4px',
                           border: '1px solid #fecaca'
                         }}>
                           Firmas
                         </span>
                       )}
                       {steps[currentStep]?.id === 'reclassification' && (
                         <span style={{
                           marginLeft: '8px',
                           fontSize: '12px',
                           fontWeight: '500',
                           color: '#8b5cf6',
                           background: '#f3e8ff',
                           padding: '2px 6px',
                           borderRadius: '4px',
                           border: '1px solid #c4b5fd'
                         }}>
                           Nota 4
                         </span>
                       )}
                       {steps[currentStep]?.id !== 'validation' && steps[currentStep]?.id !== 'reclassification' && currentPdfPage === 25 && (
                         <span style={{
                           marginLeft: '8px',
                           fontSize: '12px',
                           fontWeight: '500',
                           color: '#8b5cf6',
                           background: '#f3e8ff',
                           padding: '2px 6px',
                           borderRadius: '4px',
                           border: '1px solid #c4b5fd'
                         }}>
                           Nota 4
                         </span>
                       )}
                       {steps[currentStep]?.id !== 'validation' && steps[currentStep]?.id !== 'reclassification' && currentPdfPage === 26 && (
                         <span style={{
                           marginLeft: '8px',
                           fontSize: '12px',
                           fontWeight: '500',
                           color: '#10b981',
                           background: '#f0fdf4',
                           padding: '2px 6px',
                           borderRadius: '4px',
                           border: '1px solid #86efac'
                         }}>
                           Nota 5
                         </span>
                       )}
                     </h3>
                     <p style={{ 
                       margin: 0, 
                       color: '#6b7280', 
                       fontSize: '12px' 
                     }}>
                       {steps[currentStep]?.id === 'validation' 
                         ? 'Estados Financieros 2022-2023 • Página de Firmas y Validación'
                         : steps[currentStep]?.id === 'reclassification'
                         ? 'Estados Financieros 2022-2023 • Nota 4 - Efectivo y equivalentes'
                         : currentPdfPage === 25 
                         ? 'Estados Financieros 2022-2023 • Página 25 - Efectivo y equivalentes'
                         : currentPdfPage === 26
                         ? 'Estados Financieros 2022-2023 • Página 26 - Activos financieros'
                         : `Estados Financieros 2022-2023 • Página ${currentPdfPage}`
                       }
                     </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {/* Page Navigation - Solo mostrar para secciones con navegación */}
                  {steps[currentStep]?.id !== 'validation' && steps[currentStep]?.id !== 'reclassification' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPdfPage - 1))}
                        disabled={currentPdfPage <= 1 || pdfLoading}
                        style={{
                          background: currentPdfPage <= 1 || pdfLoading ? '#f3f4f6' : '#e5e7eb',
                          color: currentPdfPage <= 1 || pdfLoading ? '#9ca3af' : '#374151',
                          border: 'none',
                          padding: '4px 6px',
                          borderRadius: '4px',
                          cursor: currentPdfPage <= 1 || pdfLoading ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ←
                      </button>
                      
                      <span style={{
                        fontSize: '10px',
                        color: pdfLoading ? '#8b5cf6' : '#6b7280',
                        fontWeight: '500',
                        minWidth: '40px',
                        textAlign: 'center',
                        transition: 'color 0.2s ease'
                      }}>
                        {pdfLoading ? '...' : currentPdfPage}
                      </span>
                      
                      <button
                        onClick={() => handlePageChange(currentPdfPage + 1)}
                        disabled={pdfLoading}
                        style={{
                          background: pdfLoading ? '#f3f4f6' : '#e5e7eb',
                          color: pdfLoading ? '#9ca3af' : '#374151',
                          border: 'none',
                          padding: '4px 6px',
                          borderRadius: '4px',
                          cursor: pdfLoading ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        →
                      </button>
                    </div>
                  )}

                  {/* Indicador para secciones especiales */}
                  {(steps[currentStep]?.id === 'validation' || steps[currentStep]?.id === 'reclassification') && (
                    <div style={{ 
                      marginRight: '8px',
                      fontSize: '10px',
                      color: '#8b5cf6',
                      fontWeight: '500',
                      padding: '4px 8px',
                      background: '#f3e8ff',
                      borderRadius: '12px',
                      border: '1px solid #c4b5fd'
                    }}>
                      📄 Documento específico
                    </div>
                  )}

                  <button
                    onClick={() => {
                      const currentStepId = steps[currentStep]?.id;
                      let urlToOpen;
                      
                      switch (currentStepId) {
                        case 'validation':
                          urlToOpen = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-9.pdf';
                          break;
                        case 'reclassification':
                          urlToOpen = 'https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023%20(%20EJEMPLO%201%20)%20(1)-25%20(1).pdf';
                          break;
                        default:
                          urlToOpen = `https://storage.googleapis.com/mantis-documents/Estados-Financieros-ano-2022-2023.pdf#page=${currentPdfPage}`;
                      }
                      
                      window.open(urlToOpen, '_blank');
                    }}
                    disabled={pdfLoading}
                    style={{
                      background: pdfLoading ? '#94a3b8' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: pdfLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease',
                      opacity: pdfLoading ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!pdfLoading) {
                        e.target.style.background = '#2563eb';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!pdfLoading) {
                        e.target.style.background = '#3b82f6';
                      }
                    }}
                  >
                    <Eye size={12} />
                    {steps[currentStep]?.id === 'validation' 
                      ? 'Abrir Firmas'
                      : steps[currentStep]?.id === 'reclassification'
                      ? 'Abrir Nota 4'
                      : `Abrir p.${currentPdfPage}`
                    }
                  </button>
                  <button
                    onClick={() => setShowDocumentPreview(false)}
                    style={{
                      background: '#f3f4f6',
                      color: '#6b7280',
                      border: 'none',
                      padding: '6px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#e5e7eb';
                      e.target.style.color = '#374151';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#f3f4f6';
                      e.target.style.color = '#6b7280';
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

                            {/* Document Preview Content */}
              <div style={{ 
                flex: 1, 
                overflow: 'hidden',
                background: 'white',
                margin: '8px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                position: 'relative'
              }}>
                {pdfLoading && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(139, 92, 246, 0.9)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Página {currentPdfPage}
                  </div>
                )}
                
                <iframe
                  key={`pdf-preview-${steps[currentStep]?.id}-${currentPdfPage}`}
                  src={getPdfUrl()}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                    opacity: pdfLoading ? 0.7 : 1,
                    transition: 'opacity 0.15s ease'
                  }}
                  title="Vista previa del documento financiero"
                  onLoad={() => {
                    setPdfLoading(false);
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="navigation-controls">
        <button
          className="nav-btn secondary"
          onClick={() => handleNavigation('prev')}
          disabled={currentStep === 0 || isTransitioning}
        >
          ← Anterior
        </button>
        
        <div className="step-info">
          <span className="step-counter">
            Paso {currentStep + 1} de {steps.length}
          </span>
          <span className="step-name">
            {steps[currentStep].title}
          </span>
        </div>
        
        <button
          className="nav-btn primary"
          onClick={() => handleNavigation('next')}
          disabled={
            currentStep === steps.length - 1 || 
            (currentStep === 0 && uploadedFiles.length === 0) ||
            isTransitioning
          }
        >
          Siguiente →
        </button>
      </div>

      {/* Financial Analysis Chat */}
      <FinancialAnalysisChat />
    </div>
  );
};

export default FinancialAnalysis; 